import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { fetchIdeas, fetchCanonDocument } from '../lib/api'
import CanonSidebar from '../components/CanonSidebar'
import MarkdownRenderer from '../components/MarkdownRenderer'

interface Idea {
  id: string
  name: string
  canon_files: string[]
  phase: string
}

interface CanonDocument {
  idea: string
  document: string
  content: string
  last_modified: string
  line_count: number
}

export default function CanonPage() {
  const { idea: selectedIdea, doc: selectedDoc } = useParams<{ idea?: string; doc?: string }>()
  const [ideas, setIdeas] = useState<Idea[]>([])
  const [canonDoc, setCanonDoc] = useState<CanonDocument | null>(null)
  const [tableOfContents, setTableOfContents] = useState<Array<{ id: string; level: number; text: string }>>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch ideas list on mount
  useEffect(() => {
    fetchIdeas()
      .then((data) => setIdeas(data))
      .catch((err) => setError(err.message))
  }, [])

  // Fetch canon document when idea and doc are selected
  useEffect(() => {
    if (!selectedIdea || !selectedDoc) {
      setCanonDoc(null)
      setTableOfContents([])
      return
    }

    setLoading(true)
    setError(null)
    fetchCanonDocument(selectedIdea, selectedDoc)
      .then((data) => {
        setCanonDoc(data)
        setLoading(false)
      })
      .catch((err) => {
        setError(err.message)
        setLoading(false)
      })
  }, [selectedIdea, selectedDoc])

  const handleTocClick = (id: string) => {
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  return (
    <div className="flex flex-1 min-h-0">
      <CanonSidebar
        ideas={ideas}
        selectedIdea={selectedIdea || null}
        selectedDoc={selectedDoc || null}
        tableOfContents={tableOfContents}
        onTocClick={handleTocClick}
      />

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto p-8">
          {!selectedIdea && !selectedDoc && (
            <div className="text-center py-20">
              <h1 className="text-3xl font-bold text-white mb-4">Canon Documents</h1>
              <p className="text-gray-400">
                Select an Idea from the sidebar to view its canon documents
              </p>
            </div>
          )}

          {selectedIdea && !selectedDoc && (
            <div className="text-center py-20">
              <h1 className="text-3xl font-bold text-white mb-4">
                {ideas.find((i) => i.id === selectedIdea)?.name || selectedIdea}
              </h1>
              <p className="text-gray-400">
                Select a canon document from the sidebar to view its content
              </p>
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          )}

          {error && (
            <div className="bg-red-900/20 border border-red-700 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-red-400 mb-2">Error</h2>
              <p className="text-red-300">{error}</p>
            </div>
          )}

          {canonDoc && !loading && (
            <>
              {/* Document Header */}
              <div className="mb-8 pb-6 border-b border-gray-800">
                <div className="flex items-start justify-between">
                  <div>
                    <h1 className="text-3xl font-bold text-white mb-2">
                      {canonDoc.idea} — {canonDoc.document}
                    </h1>
                    <div className="flex items-center gap-4 text-sm text-gray-400">
                      <span>Last modified: {canonDoc.last_modified}</span>
                      <span>•</span>
                      <span>{canonDoc.line_count.toLocaleString()} lines</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Document Content */}
              <MarkdownRenderer
                content={canonDoc.content}
                onTocChange={setTableOfContents}
              />
            </>
          )}
        </div>
      </div>
    </div>
  )
}
